import { useEffect, useState } from "react";
import type { NearbyVenue, VenueSearchResult } from "@touchscreen/api-client";
import { TouchscreenApiClient } from "@touchscreen/api-client";
import { useVenueSearch } from "@touchscreen/hooks";
import { getFreshPosition } from "../lib/geolocation.js";
import { isVenueCode } from "../lib/venueCode.js";
import "./EntryScreen.css";

const SEARCH_MIN_LENGTH = 5;

export interface EntryScreenProps {
  baseUrl: string;
  /** `viaNearby` marks a selection as just location-verified (Find Me's confirm step) — the
   * caller uses it to skip the request flow's own geofence check for this venue. */
  onVenueChosen: (venueRef: string, opts?: { viaNearby?: boolean }) => void;
}

type FindMeState =
  | { step: "prompt" }
  | { step: "locating" }
  | { step: "denied" }
  | { step: "unsupported" }
  | { step: "results"; venues: NearbyVenue[] }
  | { step: "empty" }
  | { step: "confirm"; venue: NearbyVenue };

/**
 * No venue in the URL and nothing resumable. Find Me is the main reason someone with a peeling
 * QR code can still use the product — always ask for location with context first, and always
 * let a punter confirm the venue before committing (Nearby is a suggestion, not a certainty).
 */
export function EntryScreen(props: EntryScreenProps): React.JSX.Element {
  const [find, setFind] = useState<FindMeState>({ step: "prompt" });
  const [manualQuery, setManualQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const trimmedQuery = manualQuery.trim();
  const isCode = isVenueCode(trimmedQuery);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(manualQuery), 300);
    return () => clearTimeout(handle);
  }, [manualQuery]);

  // A code-shaped entry ("JUKE1234") skips straight to a session — it's a known ref, not
  // something to look up. Anything else goes through the general venue search.
  const { data: results, isFetching: searching } = useVenueSearch(props.baseUrl, debouncedQuery, {
    enabled: !isCode,
    minLength: SEARCH_MIN_LENGTH,
  });

  async function startFindMe(): Promise<void> {
    setFind({ step: "locating" });
    const position = await getFreshPosition();
    if (position.status === "denied") {
      setFind({ step: "denied" });
      return;
    }
    if (position.status === "unsupported") {
      setFind({ step: "unsupported" });
      return;
    }
    if (position.status === "error") {
      setFind({ step: "unsupported" });
      return;
    }

    const client = new TouchscreenApiClient({ baseUrl: props.baseUrl, surface: "phone" });
    const venues = await client.venuesNearby(position.lat, position.lng);
    setFind(venues.length === 0 ? { step: "empty" } : { step: "results", venues });
  }

  function handleManualSubmit(e: React.FormEvent): void {
    e.preventDefault();
    // Free-text results are chosen by tapping one, not by submitting the form — Enter only does
    // something once the typed text is itself a resolvable code.
    if (isCode) props.onVenueChosen(trimmedQuery.toUpperCase());
  }

  function chooseSearchResult(result: VenueSearchResult): void {
    props.onVenueChosen(result.ref);
  }

  return (
    <div className="entry-screen">
      <h1 className="entry-screen__title">Request a track</h1>
      <p className="entry-screen__lead">Find the venue you're in to get started.</p>

      {find.step === "prompt" ? (
        <button className="entry-screen__primary" onClick={startFindMe}>
          Use my location
        </button>
      ) : null}

      {find.step === "locating" ? <p className="entry-screen__hint">Finding venues nearby…</p> : null}

      {find.step === "denied" ? (
        <p className="entry-screen__hint">
          No problem — enter the code from the table talker instead.
        </p>
      ) : null}

      {find.step === "unsupported" ? (
        <p className="entry-screen__hint">Location isn't available on this device — enter the code instead.</p>
      ) : null}

      {find.step === "empty" ? <p className="entry-screen__hint">No venues found nearby — enter the code instead.</p> : null}

      {find.step === "results" ? (
        <ul className="entry-screen__results">
          {find.venues.map((venue) => (
            <li key={venue.ref}>
              <button className="entry-screen__venue" onClick={() => setFind({ step: "confirm", venue })}>
                <span>{venue.name}</span>
                <span className="entry-screen__distance">{venue.distance_miles.toFixed(1)} mi</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {find.step === "confirm" ? (
        <div className="entry-screen__confirm">
          <p>Is this your venue?</p>
          <p className="entry-screen__confirm-name">{find.venue.name}</p>
          <button
            className="entry-screen__primary"
            onClick={() => props.onVenueChosen(find.venue.ref, { viaNearby: true })}
          >
            Yes, this is it
          </button>
          <button className="entry-screen__secondary" onClick={() => setFind({ step: "results", venues: [find.venue] })}>
            No, go back
          </button>
        </div>
      ) : null}

      <form className="entry-screen__manual" onSubmit={handleManualSubmit}>
        <label htmlFor="venue-search">Or search for your venue by name, or enter the code from the table talker</label>
        <div className="entry-screen__manual-row">
          <input
            id="venue-search"
            value={manualQuery}
            onChange={(e) => setManualQuery(e.target.value)}
            placeholder="Venue name or JUKE1234"
            autoCapitalize="words"
          />
          {isCode ? (
            <button type="submit" disabled={!trimmedQuery}>
              Go
            </button>
          ) : null}
        </div>

        {!isCode && trimmedQuery && trimmedQuery.length < SEARCH_MIN_LENGTH ? (
          <p className="entry-screen__hint">Keep typing — at least {SEARCH_MIN_LENGTH} characters needed to search.</p>
        ) : null}

        {!isCode && searching ? <p className="entry-screen__hint">Searching…</p> : null}

        {!isCode && !searching && results && results.length === 0 ? (
          <p className="entry-screen__hint">No venues matched — try a different name, or enter the code instead.</p>
        ) : null}

        {!isCode && results && results.length > 0 ? (
          <ul className="entry-screen__results">
            {results.map((result) => (
              <li key={result.ref}>
                <button type="button" className="entry-screen__venue" onClick={() => chooseSearchResult(result)}>
                  <span>
                    {result.name}
                    <span className="entry-screen__venue-meta">{result.business_name} · {result.location}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </form>
    </div>
  );
}
